import { useEffect, useState } from 'react';
import { affiliate, ApiError } from '@repletoryreach/sdk-api';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';

/** Where a code from a `?ref=` link waits until the account exists. */
export const SIGNUP_REF_KEY = 'rr.signupRef';
/** Set when somebody arrived from the partners page, so onboarding ends on Refer and earn. */
export const SIGNUP_PARTNER_KEY = 'rr.signupPartner';

/**
 * The referral-code field on the sign-up form.
 *
 * It checks the code as it is typed and shows the partner's name underneath,
 * so a mistyped code is caught while somebody can still fix it rather than
 * silently earning nobody anything. A code that is not ours is a hint, never
 * a refusal: the person signing up is not the one who got it wrong, and their
 * account must not depend on it.
 */
export function ReferralCodeField({ value, onChange }: {
  value: string;
  onChange: (code: string) => void;
}) {
  const t = useT();
  const [partner, setPartner] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false);

  useEffect(() => {
    const code = value.trim().toUpperCase().replace(/[\s-]/g, '');
    setPartner(null);
    setUnknown(false);
    if (code.length < 4) return;
    let live = true;
    const timer = window.setTimeout(async () => {
      try {
        const res = await affiliate.lookupCode(code);
        if (live) setPartner(res.data.display_name);
      } catch (e) {
        if (live && e instanceof ApiError && e.status === 404) setUnknown(true);
      }
    }, 350);
    return () => { live = false; window.clearTimeout(timer); };
  }, [value]);

  return (
    <TextInput
      label={t('auth.signup.form.referralCode')}
      description={
        partner ? t('auth.signup.form.referredBy', { name: partner })
          : unknown ? t('auth.signup.form.referralUnknown')
          : t('auth.signup.form.referralCodeHint')
      }
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={t('auth.signup.form.referralCodePlaceholder')}
      maxLength={24}
      autoComplete="off"
    />
  );
}
